import { recoveryPoolInnerCall } from "./sdk";
import { ethers } from "ethers";
// The pool entry point, declared here so this helper stays free of the
// proving modules that privateFees.ts loads.
export const SETTLE_PRIVATE_FEES_INTERFACE = new ethers.Interface([
  "function settlePrivateFees((bytes32 merkleRoot,bytes32[] nullifiers,bytes32[] commitments,address token,address recipient,uint256 publicAmount,bytes proof) data,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes feeProof,bytes quoteSignature)",
]);
import { PRIVATE_TOKEN_RELAY_INTERFACE } from "./privatePools";
import {
  PRIVATE_BRIDGE_INTERFACE,
  PRIVATE_CCIP_BRIDGE_INTERFACE,
} from "./privateBridgeConfig";
import { PRIVATE_SWAP_INTERFACE } from "./privateSwapConfig";
import { SHIELDED_POOL_JOINSPLIT_V2_INTERFACE } from "@stealthos/sdk";

/** The pool settlement a transaction carries, in canonical form. The same
 * settlement (root, nullifiers, commitments, token, recipient, amount and
 * proof) can travel directly to the pool as settlePrivateFees, inside the
 * private token relay's settle bundle, or inside a bridge or swap
 * coordinator's execute call: the relayer wraps what the wallet journaled. */
export interface SettlementIntent {
  merkleRoot: string;
  nullifiers: string[];
  commitments: string[];
  token: string;
  recipient: string;
  publicAmount: string;
  proof: string;
}

const hex = (value: unknown) => String(value).toLowerCase();
function canonical(settlement: any): SettlementIntent | null {
  try {
    const intent: SettlementIntent = {
      merkleRoot: hex(settlement.merkleRoot),
      nullifiers: Array.from(settlement.nullifiers, hex),
      commitments: Array.from(settlement.commitments, hex),
      token: ethers.getAddress(String(settlement.token)).toLowerCase(),
      recipient: ethers.getAddress(String(settlement.recipient)).toLowerCase(),
      publicAmount: BigInt(settlement.publicAmount).toString(),
      proof: hex(settlement.proof),
    };
    if (
      !ethers.isHexString(intent.merkleRoot, 32) ||
      !intent.nullifiers.every((n) => ethers.isHexString(n, 32)) ||
      !intent.commitments.every((c) => ethers.isHexString(c, 32)) ||
      !ethers.isHexString(intent.proof)
    )
      return null;
    return intent;
  } catch {
    return null;
  }
}

/** The pool's own settlement structs (joinsplit, migrate, withdraw,
 * withdrawBatch, privateBridge, privateSwap) predate the fee settlement and
 * carry no recipient/amount pair, so they are canonicalised to the fields
 * every shape shares. Matching uses nullifiers and proof only. */
function canonicalPlain(data: any): SettlementIntent | null {
  try {
    const commitments = data.commitments
      ? Array.from(data.commitments, hex)
      : data.changeCommitment !== undefined &&
          BigInt(data.changeCommitment) !== 0n
        ? [hex(data.changeCommitment)]
        : [];
    const intent: SettlementIntent = {
      merkleRoot: hex(data.merkleRoot),
      nullifiers: Array.from(data.nullifiers, hex),
      commitments,
      token: ethers.getAddress(String(data.token)).toLowerCase(),
      recipient:
        data.recipient !== undefined
          ? ethers.getAddress(String(data.recipient)).toLowerCase()
          : ethers.ZeroAddress,
      publicAmount:
        data.amount !== undefined ? BigInt(data.amount).toString() : "0",
      proof: hex(data.proof),
    };
    if (
      !ethers.isHexString(intent.merkleRoot, 32) ||
      !intent.nullifiers.every((n) => ethers.isHexString(n, 32)) ||
      !intent.commitments.every((c) => ethers.isHexString(c, 32)) ||
      !ethers.isHexString(intent.proof)
    )
      return null;
    return intent;
  } catch {
    return null;
  }
}

const POOL_SETTLEMENTS = [
  "joinsplit",
  "migrate",
  "withdraw",
  "withdrawBatch",
  "privateBridge",
  "privateSwap",
];
/** settleBatch item kinds, in the pool's order. */
const BATCH_KINDS = ["joinsplit", "migrate", "withdraw", "withdrawBatch"];
const CODER = ethers.AbiCoder.defaultAbiCoder();
/** The settlements of a settleBatch call: each item is a kind and the
 * ABI-encoded arguments of that kind's pool function. Items that do not
 * round-trip exactly are ignored. */
function batchItemIntents(items: Iterable<any>): SettlementIntent[] {
  const intents: SettlementIntent[] = [];
  for (const item of Array.from(items)) {
    const name = BATCH_KINDS[Number(item.kind)];
    const fragment = name
      ? SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.getFunction(name)
      : null;
    if (!fragment) continue;
    try {
      const decoded = CODER.decode(fragment.inputs, item.data);
      if (
        CODER.encode(fragment.inputs, decoded).toLowerCase() !==
        String(item.data).toLowerCase()
      )
        continue;
      const intent = canonicalPlain(decoded[0]);
      if (intent) intents.push(intent);
    } catch {
      /* not a canonical batch item */
    }
  }
  return intents;
}

const CARRIERS: Array<{
  iface: ethers.Interface;
  names: string[];
  pick: (args: any) => unknown[];
}> = [
  {
    iface: SETTLE_PRIVATE_FEES_INTERFACE,
    names: ["settlePrivateFees"],
    pick: (args) => [args.data],
  },
  // The token relay bundle settles two pools at once: the token pool leg and
  // the gas leg. A wallet journals each leg in its own pool scope.
  {
    iface: PRIVATE_TOKEN_RELAY_INTERFACE,
    names: ["settle"],
    pick: (args) => [args.tokenLeg?.settlement, args.gasLeg?.settlement],
  },
  {
    iface: PRIVATE_BRIDGE_INTERFACE,
    names: ["execute", "recover"],
    pick: (args) => [args.leg?.settlement],
  },
  {
    iface: PRIVATE_CCIP_BRIDGE_INTERFACE,
    names: ["execute", "recover"],
    pick: (args) => [args.leg?.settlement],
  },
  {
    iface: PRIVATE_SWAP_INTERFACE,
    names: ["execute", "recover"],
    pick: (args) => [args.leg?.settlement],
  },
];
/** Carriers whose settlements are the pool's plain structs: the pool's own
 * entry points, and the relayer's settleBatch of them. */
const PLAIN_CARRIERS: Array<{
  iface: ethers.Interface;
  names: string[];
  pick: (args: any) => SettlementIntent[];
}> = [
  {
    iface: SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
    names: POOL_SETTLEMENTS,
    pick: (args) =>
      [canonicalPlain(args[0])].filter(
        (intent): intent is SettlementIntent => intent !== null,
      ),
  },
  {
    iface: SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
    names: ["settleBatch"],
    pick: (args) => batchItemIntents(args[0]),
  },
];

/** Decodes every settlement carried by calldata, or an empty list when the
 * calldata is not one of the known carriers. Only the exact round-trip
 * encoding of a known function is accepted, so a selector collision cannot
 * forge one. */
export function settlementIntentsOf(data: string): SettlementIntent[] {
  if (!ethers.isHexString(data) || ethers.dataLength(data) < 4) return [];
  try {
    data = recoveryPoolInnerCall(data);
  } catch {
    return [];
  }
  for (const carrier of CARRIERS) {
    let parsed: ethers.TransactionDescription | null;
    try {
      parsed = carrier.iface.parseTransaction({ data });
    } catch {
      continue;
    }
    if (!parsed || !carrier.names.includes(parsed.name)) continue;
    try {
      if (
        carrier.iface
          .encodeFunctionData(parsed.fragment, parsed.args)
          .toLowerCase() !== data.toLowerCase()
      )
        continue;
    } catch {
      continue;
    }
    return carrier
      .pick(parsed.args)
      .map((settlement) => (settlement ? canonical(settlement) : null))
      .filter((intent): intent is SettlementIntent => intent !== null);
  }
  for (const carrier of PLAIN_CARRIERS) {
    let parsed: ethers.TransactionDescription | null;
    try {
      parsed = carrier.iface.parseTransaction({ data });
    } catch {
      continue;
    }
    if (!parsed || !carrier.names.includes(parsed.name)) continue;
    try {
      if (
        carrier.iface
          .encodeFunctionData(parsed.fragment, parsed.args)
          .toLowerCase() !== data.toLowerCase()
      )
        continue;
    } catch {
      continue;
    }
    return carrier.pick(parsed.args);
  }
  return [];
}

/** The single settlement of a wallet-journaled transaction, or null. */
export function settlementIntentOf(data: string): SettlementIntent | null {
  return settlementIntentsOf(data)[0] ?? null;
}

/** The proof binds every other public field of a settlement, so its
 * nullifiers and proof identify it whatever carrier shape it travelled in. */
export const settlementKey = (intent: SettlementIntent) =>
  `${intent.nullifiers.join(",")}:${intent.proof}`;

export function sameSettlementIntent(
  a: SettlementIntent | null,
  b: SettlementIntent | null,
): boolean {
  return (
    !!a &&
    !!b &&
    a.nullifiers.length > 0 &&
    settlementKey(a) === settlementKey(b)
  );
}

/** A confirmed transaction proves a journal record when it is the journaled
 * transaction byte for byte, or when it carries the journaled pool
 * settlement (same nullifiers and proof) in any carrier shape, which is how
 * the relayer submits it. The proof bytes are part of the settlement, so no
 * other transaction can carry the same one. */
export function transactionMatchesRecord(
  transaction: {
    to: string | null;
    data: string;
    value: bigint;
    chainId: bigint | number;
  },
  record: {
    chainId: bigint | number;
    transaction: { to: string; data: string; value: string | bigint };
  },
): boolean {
  if (BigInt(transaction.chainId) !== BigInt(record.chainId)) return false;
  // A different carrier may pay its own coordinator fee, but an unchanged
  // envelope must retain its exact value. Do not let intent fallback turn a
  // mismatched payable bridge transaction into successful journal recovery.
  if (
    transaction.to !== null &&
    ethers.getAddress(transaction.to) ===
      ethers.getAddress(record.transaction.to) &&
    transaction.data.toLowerCase() === record.transaction.data.toLowerCase()
  )
    return transaction.value === BigInt(record.transaction.value);
  const journaled = settlementIntentOf(record.transaction.data);
  return settlementIntentsOf(transaction.data).some((carried) =>
    sameSettlementIntent(carried, journaled),
  );
}
